// Bienvenida por WhatsApp: mensaje y video que el agente manda la PRIMERA vez
// que alguien escribe. Antes vivía en Empresa → Configuración → WhatsApp; ahora
// es parte de la app (Configuración → Bienvenida). Sin app (la org todavía no
// tiene número) se sigue mostrando en Empresa, con el mismo componente.
//
// Datos: mcp_django /api/v1/admin/company/whatsapp-config (Company de la org
// del JWT). El PUT es parcial (exclude_unset): solo se mandan los campos que
// cambiaron, así no se pisa nada que otro haya guardado mientras tanto.

import { useEffect, useState } from 'react';
import { Loader2, MessageCircle, Video } from 'lucide-react';

import { getWhatsAppConfig, updateWhatsAppConfig, type WhatsAppConfig } from '@/lib/api_travel';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

const field = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-70';

export function WhatsAppWelcomeSettings({ notify, canEdit = true }: { notify: Notify; canEdit?: boolean }) {
  const [initial, setInitial] = useState<WhatsAppConfig | null>(null);
  const [cfg, setCfg] = useState<WhatsAppConfig>({ mensaje_bienvenida: '', video_bienvenida_url: '' });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getWhatsAppConfig()
      .then((r) => {
        const v = { mensaje_bienvenida: r?.mensaje_bienvenida || '', video_bienvenida_url: r?.video_bienvenida_url || '' };
        setInitial(v); setCfg(v);
      })
      .catch((e) => setErr(e?.message || 'No se pudo cargar la bienvenida'));
  }, []);

  const changed = (Object.keys(cfg) as (keyof WhatsAppConfig)[]).filter((k) => initial && cfg[k] !== initial[k]);

  const save = async () => {
    if (!changed.length) return;
    setSaving(true);
    try {
      const body: Partial<WhatsAppConfig> = {};
      changed.forEach((k) => { body[k] = cfg[k].trim(); });
      const r = await updateWhatsAppConfig(body);
      const v = { mensaje_bienvenida: r?.mensaje_bienvenida || '', video_bienvenida_url: r?.video_bienvenida_url || '' };
      setInitial(v); setCfg(v);
      notify('success', 'Bienvenida guardada');
    } catch (e: any) {
      notify('error', 'No se guardó la bienvenida', e?.message);
    } finally {
      setSaving(false);
    }
  };

  const videoOk = /^https?:\/\//i.test(cfg.video_bienvenida_url.trim());

  return (
    <div className="wa-app rounded-2xl border border-border bg-card p-4 text-card-foreground">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-muted-foreground" aria-hidden />
        <h3 className="text-[15px] font-semibold text-foreground">Mensaje de bienvenida</h3>
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Lo que el agente manda cuando alguien te escribe por primera vez. Si lo dejas vacío, el agente saluda con su mensaje normal.
      </p>
      {!initial && !err && <div className="mt-4 h-32 animate-pulse rounded-xl bg-muted" aria-hidden />}
      {err && <p className="mt-3 text-[13.5px] text-destructive">{err}</p>}
      {initial && (
        <div className="mt-4 space-y-3">
          <label className="block text-[12.5px] font-medium text-foreground">Mensaje
            <textarea className={`${field} mt-1`} rows={3} disabled={!canEdit} value={cfg.mensaje_bienvenida}
              placeholder="¡Hola! Gracias por escribirnos. ¿En qué te podemos ayudar?"
              onChange={(e) => setCfg((c) => ({ ...c, mensaje_bienvenida: e.target.value }))} />
          </label>
          <label className="block text-[12.5px] font-medium text-foreground">Video de bienvenida (enlace, opcional)
            <input className={`${field} mt-1`} type="url" inputMode="url" disabled={!canEdit} value={cfg.video_bienvenida_url}
              placeholder="https://…/video.mp4"
              onChange={(e) => setCfg((c) => ({ ...c, video_bienvenida_url: e.target.value }))} />
          </label>
          {videoOk && (
            <a href={cfg.video_bienvenida_url.trim()} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium underline-offset-2 hover:underline wa-accent-text">
              <Video className="h-3.5 w-3.5" aria-hidden /> Ver video
            </a>
          )}
          {canEdit ? (
            <div className="flex justify-end">
              <button type="button" onClick={save} disabled={!changed.length || saving}
                className="wa-btn inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Guardar bienvenida
              </button>
            </div>
          ) : <p className="text-[12.5px] text-muted-foreground">Solo administración puede cambiar la bienvenida.</p>}
        </div>
      )}
    </div>
  );
}
